import React, { useState } from "react";
import { NavLink } from "react-router-dom";

const cardStyle = {
  background: "#ffffff",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  padding: "20px",
  marginBottom: "20px",
};

const teal = "#06d1c6";

const INITIAL_QUESTIONS = [
  { id: 1, title: "What is your primary financial goal?", options: ["Capital protection", "Growth with moderate risk", "High growth with high risk"] },
  { id: 2, title: "What is your current age?", options: ["Below 25", "25-35", "36-45", "46-55", "56-65", "Above 65"] },
  { id: 3, title: "What is your total household income?", options: ["Below 5 Lakhs", "5-10 Lakhs", "10-20 Lakhs", "20-50 Lakhs", "Above 50 Lakhs"] },
  { id: 4, title: "What is your primary source of income?", options: ["Salary/Business", "Rental Income", "Fixed Deposits", "Pension", "Others"] },
  { id: 5, title: "What is your investment horizon?", options: ["Below 1 year", "1-3 years", "3-5 years", "5-10 years", "Above 10 years"] },
  { id: 6, title: "What is your current investment experience?", options: ["Beginner", "Intermediate", "Expert"] },
  { id: 7, title: "How would you react to a market downturn (say 20-30% fall)?", options: ["Sell off all investments", "Sell off some investments", "Hold on to investments", "Buy more investments"] },
  { id: 8, title: "Do you have an emergency fund?", options: ["Yes, for 6+ months", "Yes, for 3-6 months", "No"] },
  { id: 9, title: "What percentage of your investments are in equities?", options: ["Below 20%", "20-40%", "40-60%", "60-80%", "Above 80%"] },
  { id: 10, title: "How much risk are you willing to take for higher returns?", options: ["No risk", "Low risk", "Medium risk", "High risk", "Very high risk"] },
  { id: 11, title: "What is your financial knowledge?", options: ["Novice", "Intermediate", "Expert"] },
  { id: 12, title: "Do you have any dependents?", options: ["No", "1-2", "3-4", "5+"] },
  { id: 13, title: "What is your net worth?", options: ["Below 10 Lakhs", "10-50 Lakhs", "50 Lakhs - 1 Crore", "1-5 Crore", "Above 5 Crore"] },
  { id: 14, title: "What is your investment objective?", options: ["Wealth creation", "Income generation", "Tax saving"] },
  { id: 15, title: "What is your comfort level with market volatility?", options: ["Not comfortable", "Slightly comfortable", "Moderately comfortable", "Very comfortable"] },
];

const RISK_CATEGORIES = ["Conservative", "Moderately Conservative", "Moderate", "Moderately Aggressive", "Aggressive"];

export default function SetupRiskProfiler() {
  const [questions, setQuestions] = useState(() =>
    INITIAL_QUESTIONS.map((q) => ({
      ...q,
      options: q.options.map((label) => ({ label, pointValue: "1" })),
    }))
  );
  const [mappings, setMappings] = useState([
    { id: 1, typeLabel: "Conservative (4-12)", scoreFrom: 4, scoreTo: 12, riskCategory: "Conservative", riskDescription: "", isActive: true },
  ]);
  const [mappingType, setMappingType] = useState("Conservative (4-12)");
  const [scoreFrom, setScoreFrom] = useState("4");
  const [scoreTo, setScoreTo] = useState("12");
  const [riskCategory, setRiskCategory] = useState("Conservative");
  const [riskDescription, setRiskDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  const addOption = (questionId) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId
          ? { ...q, options: [...q.options, { label: "", pointValue: "1" }] }
          : q
      )
    );
  };

  const updateOption = (questionId, optionIndex, field, value) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== questionId) return q;
        const opts = [...q.options];
        opts[optionIndex] = { ...opts[optionIndex], [field]: value };
        return { ...q, options: opts };
      })
    );
  };

  const addNewQuestion = () => {
    const nextId = Math.max(0, ...questions.map((q) => q.id)) + 1;
    setQuestions((prev) => [...prev, { id: nextId, title: "New question?", options: [{ label: "Option 1", pointValue: "1" }] }]);
  };

  const addProfile = () => {
    setMappings((prev) => [
      ...prev,
      {
        id: Date.now(),
        typeLabel: `${riskCategory} (${scoreFrom}-${scoreTo})`,
        scoreFrom: Number(scoreFrom) || 0,
        scoreTo: Number(scoreTo) || 0,
        riskCategory,
        riskDescription,
        isActive,
      },
    ]);
    setScoreFrom("");
    setScoreTo("");
    setRiskDescription("");
  };

  const handleReset = () => {
    setQuestions(() =>
      INITIAL_QUESTIONS.map((q) => ({
        ...q,
        options: q.options.map((label) => ({ label, pointValue: "1" })),
      }))
    );
    setMappings([{ id: 1, typeLabel: "Conservative (4-12)", scoreFrom: 4, scoreTo: 12, riskCategory: "Conservative", riskDescription: "", isActive: true }]);
    setMappingType("Conservative (4-12)");
    setScoreFrom("4");
    setScoreTo("12");
    setRiskCategory("Conservative");
    setRiskDescription("");
    setIsActive(true);
  };

  const inputStyle = {
    padding: "8px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    fontSize: "14px",
    width: "100%",
    maxWidth: "100%",
  };
  const inputSmallStyle = { ...inputStyle, width: "70px", maxWidth: "70px", textAlign: "center" };

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          {/* Header */}
          <div className="mb-4" style={cardStyle}>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div>
                <h5 className="mb-1">Setup Risk Profiler</h5>
                <nav aria-label="breadcrumb" style={{ "--bs-breadcrumb-divider": "none" }}>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <NavLink to="/dashboard">Dashboard</NavLink> /
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/admin/risk-profiling/submissions">Risk Profiling</NavLink> /
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Setup Risk Profiler
                    </li>
                  </ol>
                </nav>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <button type="button" className="d-flex align-items-center gap-2 border-0 rounded px-3 py-2" style={{ background: "#f1f5f9", color: "#475569", fontSize: "14px", cursor: "pointer" }}>
                  <i className="fa fa-download" />
                  Export
                </button>
                <button type="button" className="d-flex align-items-center gap-2 border-0 rounded px-3 py-2" style={{ background: "#22c55e", color: "#fff", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>
                  <i className="fa fa-save" />
                  Save
                </button> 
                <button type="button" onClick={handleReset} className="d-flex align-items-center gap-2 border-0 rounded px-3 py-2" style={{ background: "#f1f5f9", color: "#475569", fontSize: "14px", cursor: "pointer" }}>
                  <i className="fa fa-sync-alt" />
                  Reset
                </button>
                <button type="button" onClick={addNewQuestion} className="d-flex align-items-center gap-2 border-0 rounded px-3 py-2" style={{ background: teal, color: "#fff", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>
                  <i className="fa fa-plus" />
                  Add New
                </button>
              </div>
            </div>
          </div>

          {/* Info banner */}
          <div className="mb-4 rounded py-3 px-4" style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)", color: "#fff", fontSize: "14px" }}>
            Here you can create a risk profiler questionnaire that will be sent to the client to answer before deciding on investment options that best suit them.
          </div>

          {/* Two columns */}
          <div className="row g-4">
            <div className="col-12 col-lg-7">
              <div style={{ ...cardStyle, padding: "24px" }}>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h6 className="mb-0 fw-bold" style={{ color: "#334155" }}>Questions</h6>
                  <span className="text-muted" style={{ fontSize: "13px" }}>Point Value</span>
                </div>
                <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  {questions.map((q, qIdx) => (
                    <div key={q.id} className="mb-4 pb-4" style={{ borderBottom: qIdx < questions.length - 1 ? "1px solid #e2e8f0" : "none" }}>
                      <div className="fw-bold mb-2" style={{ color: "#334155", fontSize: "14px" }}>
                        Question {qIdx + 1}: {q.title}
                      </div>
                      <div className="ps-2">
                        {q.options.map((opt, optIdx) => (
                          <div key={optIdx} className="d-flex align-items-center gap-2 mb-2">
                            <span className="text-muted" style={{ minWidth: "20px", fontSize: "13px" }}>{optIdx + 1}.</span>
                            <input
                              type="text"
                              value={opt.label}
                              onChange={(e) => updateOption(q.id, optIdx, "label", e.target.value)}
                              placeholder="Option label"
                              style={{ ...inputStyle, flex: 1, maxWidth: "100%" }}
                            />
                            <input
                              type="text"
                              inputMode="numeric"
                              value={opt.pointValue}
                              onChange={(e) => updateOption(q.id, optIdx, "pointValue", e.target.value)}
                              style={inputSmallStyle}
                              aria-label={`Point value for option ${optIdx + 1}`}
                            />
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addOption(q.id)}
                          className="btn btn-link p-0 border-0 text-decoration-none"
                          style={{ color: teal, fontSize: "13px", fontWeight: 500, cursor: "pointer" }}
                        >
                          Add more Options
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-5">
              <div style={{ ...cardStyle, padding: "24px", position: "sticky", top: "20px" }}>
                <h6 className="mb-4 fw-bold" style={{ color: "#334155" }}>Risk Profile Mapping</h6>
                <div className="mb-3">
                  <label className="form-label mb-1" style={{ fontSize: "13px", color: "#64748b" }}>Risk Profile Mapping Type:</label>
                  <select
                    value={mappingType}
                    onChange={(e) => setMappingType(e.target.value)}
                    style={inputStyle}
                  >
                    {mappings.map((m) => (
                      <option key={m.id} value={m.typeLabel}>{m.typeLabel}</option>
                    ))}
                  </select>
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label mb-1" style={{ fontSize: "13px", color: "#64748b" }}>Score From:</label>
                    <input type="number" value={scoreFrom} onChange={(e) => setScoreFrom(e.target.value)} style={inputStyle} min={0} />
                  </div>
                  <div className="col-6">
                    <label className="form-label mb-1" style={{ fontSize: "13px", color: "#64748b" }}>Score To:</label>
                    <input type="number" value={scoreTo} onChange={(e) => setScoreTo(e.target.value)} style={inputStyle} min={0} />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label mb-1" style={{ fontSize: "13px", color: "#64748b" }}>Risk Category:</label>
                  <select value={riskCategory} onChange={(e) => setRiskCategory(e.target.value)} style={inputStyle}>
                    {RISK_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label mb-1" style={{ fontSize: "13px", color: "#64748b" }}>Risk Description:</label>
                  <textarea
                    value={riskDescription}
                    onChange={(e) => setRiskDescription(e.target.value)}
                    placeholder="Enter risk description here."
                    rows={4}
                    style={{ ...inputStyle, resize: "vertical" }}
                  />
                </div>
                <div className="mb-4">
                  <label className="d-flex align-items-center gap-2 mb-0" style={{ fontSize: "13px", color: "#64748b", cursor: "pointer" }}>
                    <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} style={{ accentColor: teal, width: "18px", height: "18px" }} />
                    Is Active
                  </label>
                </div>
                <button
                  type="button"
                  onClick={addProfile}
                  className="btn rounded px-4 py-2 mb-2 border-0"
                  style={{ background: teal, color: "#fff", fontWeight: 600, fontSize: "14px", cursor: "pointer" }}
                >
                  Add Profile
                </button>
                <div>
                  <button type="button" className="btn btn-link p-0 text-decoration-none border-0" style={{ color: teal, fontSize: "13px", fontWeight: 500, cursor: "pointer" }}>
                    Download Risk Type Master
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div style={cardStyle}>
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <button type="button" className="btn rounded px-4 py-2 border-0" style={{ background: "#22c55e", color: "#fff", fontWeight: 600, fontSize: "14px", cursor: "pointer" }}>
                Save
              </button>
              <button type="button" className="btn rounded px-4 py-2 border" style={{ background: "#fff", color: "#64748b", borderColor: "#e2e8f0", fontSize: "14px", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
            <p className="text-muted mb-0" style={{ fontSize: "13px", lineHeight: 1.5 }}>
              Note: The risk profiler questions can be edited/updated at any point in time. If a question is edited/updated, please make sure to also update the corresponding Risk Profile Mapping in order for the changes to reflect accurately.
            </p>
            <div className="mt-3">
              <button type="button" className="btn rounded px-4 py-2 border-0" style={{ background: "#22c55e", color: "#fff", fontWeight: 600, fontSize: "14px", cursor: "pointer" }}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
